import useOptions from "features/masterData/hooks/useOptions";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { generateRandomString } from "utils/helper";
import * as services from "../services/reportApi";
import { setIsLoading, setReportTableList, setReportWarningLetterList } from "../slices/reportSlice";

function useEmployeeWarningLetter() {
    const { t, i18n } = useTranslation();
    const { employeeTypeOptions, employmentTypeOptions, getEmployeeTypeOptions, getEmploymentTypeOptions } = useOptions();
    const { employeeStatusTypeOptions, getEmployeeStstuaTypeOptions } = useOptions();
    const { informationTypeOptions, getInformationTypeOptions } = useOptions();

    const dispatch = useDispatch();
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const reportWarningLetter = useSelector((state) => state.report.reportWarningLetter || []);
    const [showReport, setShowReport] = useState(false);
    const isLoading = useSelector((state) => state.employee.isLoading);

    const [positionOptions, setPositionOptions] = useState([]);
    const [departmentListOption, setDepartmentListOption] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const [search, setSearch] = useState({});
    const [selectedDates, setSelectedDates] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleDateChange = (dates) => {
        setSelectedDates(dates);
    };

    function toParams(params = {}) {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            pagination: {
                page: pagination?.page,
                size: pagination?.pageSize,
            },
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;
    }
    const currentEmployee = useSelector((state) => state.company.currentEmployee);

    const modifiedPositionOptions = useMemo(() => {
        const modifiedOptions = positionOptions?.map((opt) => ({
            ...opt,
            id: opt?.positionId,
            value: opt?.positionId,
            nameEn: opt?.positionNameEn,
            name: opt?.positionName,
            label: i18n.language === "en" ? opt?.positionNameEn : opt?.positionName,
        }));
        return modifiedOptions;
    }, [i18n, positionOptions]);

    const modifiedDepartmentOption = useMemo(() => {
        const modifiedOptions = departmentListOption?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions;
    }, [i18n.language, departmentListOption]);

    const getPosition = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getPosition(values);
                setPositionOptions(response.data);
            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);


    const getDepartment = useCallback(async () => {
        if (!currentCompany) return null;
        try {
            dispatch(setIsLoading(true));
            const response = await services.getDepartmentOptions({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setDepartmentListOption(response.data));
        } catch (error) {
            // alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    useEffect(() => {
        if (currentCompany) {
            getEmployeeTypeOptions({ companyId: currentCompany, groupType: "EMPLOYEE_TYPE" });
            getEmployeeStstuaTypeOptions();
            getInformationTypeOptions();
            getEmploymentTypeOptions({ companyId: currentCompany });
            getDepartment();
            getPosition();
        }
    }, [currentCompany, getEmployeeTypeOptions, getEmployeeStstuaTypeOptions, getEmploymentTypeOptions, getEmployeeTypeOptions, getPosition]);

    const getWarningLetter = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getReportWarningLetter({
                    requestId: generateRandomString(),
                    employeeId: currentEmployee,
                    companyId: currentCompany,
                    isReport: "",
                    ...params,
                    informationType: 1,
                    ...params.pagination,
                });

                dispatch(setReportWarningLetterList(response?.data?.page?.content));

                setPagination({
                    ...pagination,
                    // ...params?.request,
                    current: response?.data?.page?.pageable?.pageNumber + 1,
                    pageNo: response?.data?.page?.pageable?.pageNumber + 1,
                    size: response?.data?.page?.pageable?.pageSize,
                    total: response?.data?.page?.totalElements,
                });
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, pagination]
    );


    const onChange = useCallback(
        (tablePagination, tableFilters, tableSorter, tableExtra) => {

            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];
            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination.current - 1,
                    pageSize: tablePagination.pageSize,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            setPagination({ ...pagination, ...modifiedParams?.pagination })
            getWarningLetter({ ...modifiedParams, request: pagination.request, ...search });
        },
        [getWarningLetter, pagination, search]
    );

    const exportEmployeeWarningLetterReport = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                services.ExportEmployeeWarningLetterPaging({
                    ...params,
                    companyId: currentCompany,
                    informationType: 1,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y"
                }).then(res => {
                    const url = window.URL.createObjectURL(res?.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', "employee-warning-letter.xlsx");
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                });
            } catch (error) {
                console.error("Error fetching report warning letter:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
            console.log("params", search);
        },
        [currentCompany, dispatch, search]
    );

    const handleSearch = (values) => {
        setShowReport(true)
        setSearch({})

        try {
            setSearch({
                ...values,
                startDate: values?.startToEndDate?.[0] ? values?.startToEndDate?.[0]?.format('YYYY-MM-DD') : null,
                endDate: values?.startToEndDate?.[1] ? values?.startToEndDate?.[1]?.format('YYYY-MM-DD') : null,
                isReport: values?.isReport || "",
            })
            getWarningLetter({
                ...values,
                startDate: values?.startToEndDate?.[0] ? values?.startToEndDate?.[0]?.format('YYYY-MM-DD') : null,
                endDate: values?.startToEndDate?.[1] ? values?.startToEndDate?.[1]?.format('YYYY-MM-DD') : null,
                ...pagination,
                year: values?.year?.format("YYYY"),
            })
        } catch (error) {
            console.log("error", error);

        }
    };

    return {
        handleSearch,
        exportEmployeeWarningLetterReport,
        showReport,
        onChange,
        pagination,
        employeeTypeOptions,
        getWarningLetter,
        getEmployeeTypeOptions,
        reportWarningLetter,
        employeeStatusTypeOptions,
        informationTypeOptions,
        departmentOptions: modifiedDepartmentOption,
        positionOptions: modifiedPositionOptions,
        employmentTypeOptions,
        isModalOpen,
        setIsModalOpen,
        showModal,
        handleDateChange
    }
}
export default useEmployeeWarningLetter;